// src/pages/BusinessRateCard.jsx
import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Table,
  Spinner,
  Badge,
} from "reactstrap"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl from "url"

const BusinessRateCard = () => {
  document.title = "Business Rate Card"
  const { businessId } = useParams()
  const navigate = useNavigate()

  // Optional auth gate (same as your other pages)
  useEffect(() => {
    const role_ = localStorage.getItem("role")
    if (role_ !== "admin") navigate("/")
  }, [navigate])

  // UI state
  const [services, setServices] = useState([])
  const [pricingCards, setPricingCards] = useState([]) // optional display of cards
  const [isLoading, setIsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedService, setSelectedService] = useState("")
  const [sourceScope, setSourceScope] = useState("general") // "business" | "general"

  // Editor state (matches your existing shapes)
  const [weightCharges, setWeightCharges] = useState([
    { from: "", to: "", within_city: "", same_zone: "", different_zone: "" },
  ])
  const [cashHandling, setCashHandling] = useState([
    { cod_from: "", cod_to: "", charge: "" },
  ])
  const [factors, setFactors] = useState({
    fsc: "",
    gst: "",
    return_within_city: "",
    return_same_zone: "",
    return_different_zone: "",
  })

  // Fetch one business by ID
  const [businesName, setBusinessName] = useState([])
  const fetchBusinessById = async businessId => {
    try {
      const res = await axios.get(`${apiURl}/fetchBusinessById`, {
        params: { business_id: businessId },
      })
      if (res.data.status === "success") {
        const business = res.data.business // null if not found
        setBusinessName(business.businessName)
        if (!business) toastr.info("Business not found")
      } else {
        toastr.error(res.data.message || "Failed to fetch business")
      }
    } catch (e) {
      toastr.error("Failed to fetch business")
    }
  }

  // Example usage (e.g., when opening a details page)
  useEffect(() => {
    if (businessId) fetchBusinessById(businessId)
  }, [businessId])

  // ---------- Fetch base data ----------
  const fetchServices = async () => {
    try {
      const res = await axios.get(`${apiURl}/fetchServiceTypes`)
      if (res.data.status === "success") setServices(res.data.services || [])
    } catch {
      toastr.error("Failed to fetch services")
    }
  }

  // Optional: show a grid of effective pricing cards for quick view
  const fetchCardsEffective = async () => {
    try {
      setIsLoading(true)

      const res = await axios.get(`${apiURl}/fetchServicePricing`, {
        params: { business_id: businessId },
      })
      if (res.data.status === "success") {
        setPricingCards(res.data.pricing || [])
      }
    } catch {
      toastr.error("Failed to fetch pricing")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
    fetchCardsEffective()
  }, [])

  // ---------- Fetch effective pricing for a single service ----------
  const fetchEffectivePricingByService = async serviceId => {
    // 1) Try business-specific (if your backend supports it)
    //    If your current backend only has /fetchServicePricingById?service_id,
    //    this first call will likely 404 or return empty. That's fine—we fallback.
    try {
      const resBiz = await axios.get(
        `${apiURl}/fetchServicePricingById?service_id=${serviceId}&business_id=${businessId}`
      )
      if (resBiz.data?.status === "success" && resBiz.data.pricing) {
        const p = resBiz.data.pricing
        const hasBizRows =
          (p?.weight_charges?.length || 0) > 0 ||
          (p?.cash_handling?.length || 0) > 0 ||
          p?.fsc_percentage != null ||
          p?.gst_percentage != null ||
          p?.return_within_city != null ||
          p?.return_same_zone != null ||
          p?.return_different_zone != null

        if (hasBizRows) {
          setSourceScope("business")
          hydrateEditorFromPayload(p)
          return
        }
      }
    } catch {
      // ignore and fall through to general
    }

    // 2) Fallback to general
    try {
      const resGen = await axios.get(
        `${apiURl}/fetchServicePricingById?service_id=${serviceId}`
      )
      if (resGen.data?.status === "success" && resGen.data.pricing) {
        setSourceScope("general")
        hydrateEditorFromPayload(resGen.data.pricing)
        return
      }
    } catch {
      // no-op
    }

    // 3) If nothing found, reset editor
    setSourceScope("general")
    resetEditor()
    toastr.info("No pricing found; showing empty defaults")
  }

  const hydrateEditorFromPayload = pricing => {
    // factors
    setFactors({
      fsc: pricing.fsc_percentage ?? "",
      gst: pricing.gst_percentage ?? "",
      return_within_city: pricing.return_within_city ?? "",
      return_same_zone: pricing.return_same_zone ?? "",
      return_different_zone: pricing.return_different_zone ?? "",
    })
    // weights
    setWeightCharges(
      pricing.weight_charges?.length
        ? pricing.weight_charges.map(w => ({
            from: w.from,
            to: w.to,
            within_city: w.within_city,
            same_zone: w.same_zone,
            different_zone: w.different_zone,
          }))
        : [
            {
              from: "",
              to: "",
              within_city: "",
              same_zone: "",
              different_zone: "",
            },
          ]
    )
    // cash
    setCashHandling(
      pricing.cash_handling?.length
        ? pricing.cash_handling.map(c => ({
            cod_from: c.cod_from,
            cod_to: c.cod_to,
            charge: c.charge,
          }))
        : [{ cod_from: "", cod_to: "", charge: "" }]
    )
  }

  const resetEditor = () => {
    setSelectedService("")
    setFactors({
      fsc: "",
      gst: "",
      return_within_city: "",
      return_same_zone: "",
      return_different_zone: "",
    })
    setWeightCharges([
      { from: "", to: "", within_city: "", same_zone: "", different_zone: "" },
    ])
    setCashHandling([{ cod_from: "", cod_to: "", charge: "" }])
  }

  // ---------- Handlers like your existing page ----------
  const handleWeightChange = (index, field, value) => {
    const rows = [...weightCharges]
    rows[index][field] = value
    setWeightCharges(rows)
  }
  const addWeightRow = () =>
    setWeightCharges([
      ...weightCharges,
      { from: "", to: "", within_city: "", same_zone: "", different_zone: "" },
    ])
  const removeWeightRow = index => {
    const rows = [...weightCharges]
    rows.splice(index, 1)
    setWeightCharges(rows)
  }

  const handleCashChange = (index, field, value) => {
    const rows = [...cashHandling]
    rows[index][field] = value
    setCashHandling(rows)
  }
  const addCashRow = () =>
    setCashHandling([...cashHandling, { cod_from: "", cod_to: "", charge: "" }])
  const removeCashRow = index => {
    const rows = [...cashHandling]
    rows.splice(index, 1)
    setCashHandling(rows)
  }

  const handleFactorChange = e => {
    const { name, value } = e.target
    setFactors(prev => ({ ...prev, [name]: value }))
  }

  // ---------- Save (creates/updates business override) ----------
  const handleSubmit = async () => {
    if (!selectedService) {
      toastr.error("Select a service type")
      return
    }

    const payload = {
      business_id: businessId, // <-- important for per-business override
      service_id: selectedService,
      weight_charges: weightCharges,
      factors,
      cash_handling: cashHandling,
    }

    try {
      const res = await axios.post(`${apiURl}/addServicePricing`, payload)
      if (res.data.status === "success") {
        toastr.success("Pricing saved successfully")
        toggleModal()
        fetchCardsEffective()
      } else {
        toastr.error(res.data.message || "Failed to save pricing")
      }
    } catch {
      toastr.error("Error saving pricing")
    }
  }

  const toggleModal = (serviceId = "") => {
    setModalOpen(!modalOpen)
    if (serviceId) {
      setSelectedService(serviceId)
      // Load effective (business → general)
      fetchEffectivePricingByService(serviceId)
    } else if (modalOpen) {
      resetEditor()
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="align-items-center">
          <Col>
            <h5 className="mt-2">
              <Badge
                pill
                className="badge-soft-danger ms-1 font-size-14"
                style={{ padding: "7px" }}
              >
                {businesName}
              </Badge>
              {""} — Rate Card
            </h5>
          </Col>
          <Col className="text-end">
            <Link to="/approved-business" className="btn btn-secondary btn-sm">
              ← Back to Businesses
            </Link>
          </Col>
        </Row>

        {/* Cards view (optional quick read) */}
        {isLoading ? (
          <div className="text-center my-4">
            <Spinner />
          </div>
        ) : (
          <Row className="mt-4">
            {pricingCards.map(service => (
              <Col md={6} key={service.service_id} className="mb-4">
                <Card>
                  <CardHeader>
                    <Row>
                      <Col lg="7">
                        <h6 className="mb-0">{service.service_name}</h6>
                      </Col>
                      <Col lg="5" className="text-end">
                        <i
                          className="bx bxs-edit"
                          style={{ cursor: "pointer" }}
                          title="Edit"
                          onClick={() => toggleModal(service.service_id)}
                        />
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody>
                    {/* Weight Charges */}
                    <h6>Weight Charges</h6>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>From</th>
                          <th>To</th>
                          <th>Within City</th>
                          <th>Same Zone</th>
                          <th>Different Zone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {service.weight_charges.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.from}</td>
                            <td>{row.to}</td>
                            <td>{row.within_city}</td>
                            <td>{row.same_zone}</td>
                            <td>{row.different_zone}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <Row>
                      <Col md={6}>
                        <div className="p-2 border rounded mb-2">
                          <strong>FSC:</strong> {service.fsc_percentage}%
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-2 border rounded mb-2">
                          <strong>GST:</strong> {service.gst_percentage}%
                        </div>
                      </Col>
                    </Row>

                    <h6 className="mt-3">Return Charges</h6>
                    <Row>
                      <Col md={4}>
                        <div className="p-2 border rounded">
                          <strong>Within City:</strong>{" "}
                          {service.return_within_city}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-2 border rounded">
                          <strong>Same Zone:</strong> {service.return_same_zone}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-2 border rounded">
                          <strong>Different Zone:</strong>{" "}
                          {service.return_different_zone}
                        </div>
                      </Col>
                    </Row>

                    <h6 className="mt-3">Cash Handling</h6>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>COD From</th>
                          <th>COD To</th>
                          <th>Charge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {service.cash_handling.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.cod_from}</td>
                            <td>{row.cod_to}</td>
                            <td>{row.charge}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Modal for Add/Edit single service (business-aware) */}
        <Modal isOpen={modalOpen} toggle={() => toggleModal()} size="xl">
          <ModalHeader toggle={() => toggleModal()}>
            {selectedService ? "Edit Service Charges" : "Set Service Charges"}
          </ModalHeader>
          <ModalBody>
            {/* Scope badge */}
            <div className="mb-2">
              <Badge
                color={sourceScope === "business" ? "success" : "secondary"}
              >
                {sourceScope === "business"
                  ? "Business Override"
                  : "Using General Defaults"}
              </Badge>
            </div>

            {/* Service Selection */}
            <FormGroup>
              <Label>Service Type *</Label>
              <Input
                type="select"
                value={selectedService}
                onChange={e => {
                  const sid = e.target.value
                  setSelectedService(sid)
                  if (sid) fetchEffectivePricingByService(sid)
                }}
              >
                <option value="">Select service…</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {/* Weight Charges */}
            <Card className="mb-3">
              <CardBody>
                <h6>Weight Charges</h6>
                {weightCharges.map((row, index) => (
                  <Row key={index} className="mb-2">
                    <Col md={2}>
                      <Input
                        placeholder="From"
                        value={row.from}
                        onChange={e =>
                          handleWeightChange(index, "from", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        placeholder="To"
                        value={row.to}
                        onChange={e =>
                          handleWeightChange(index, "to", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        placeholder="Within City"
                        value={row.within_city}
                        onChange={e =>
                          handleWeightChange(
                            index,
                            "within_city",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        placeholder="Same Zone"
                        value={row.same_zone}
                        onChange={e =>
                          handleWeightChange(index, "same_zone", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        placeholder="Different Zone"
                        value={row.different_zone}
                        onChange={e =>
                          handleWeightChange(
                            index,
                            "different_zone",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        color="danger"
                        onClick={() => removeWeightRow(index)}
                      >
                        X
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button color="success" size="sm" onClick={addWeightRow}>
                  + Add Row
                </Button>
              </CardBody>
            </Card>

            {/* FSC & GST */}
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardBody>
                    <h6>FSC (%)</h6>
                    <Input
                      type="number"
                      name="fsc"
                      value={factors.fsc}
                      onChange={handleFactorChange}
                    />
                  </CardBody>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <CardBody>
                    <h6>GST (%)</h6>
                    <Input
                      type="number"
                      name="gst"
                      value={factors.gst}
                      onChange={handleFactorChange}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Return Charges */}
            <Card className="mb-3">
              <CardBody>
                <h6>Return Charges</h6>
                <Row>
                  <Col md={4}>
                    <Input
                      placeholder="Within City"
                      name="return_within_city"
                      value={factors.return_within_city}
                      onChange={handleFactorChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Input
                      placeholder="Same Zone"
                      name="return_same_zone"
                      value={factors.return_same_zone}
                      onChange={handleFactorChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Input
                      placeholder="Different Zone"
                      name="return_different_zone"
                      value={factors.return_different_zone}
                      onChange={handleFactorChange}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Cash Handling */}
            <Card>
              <CardBody>
                <h6>Cash Handling</h6>
                {cashHandling.map((row, index) => (
                  <Row key={index} className="mb-2">
                    <Col md={3}>
                      <Input
                        placeholder="COD From"
                        value={row.cod_from}
                        onChange={e =>
                          handleCashChange(index, "cod_from", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Input
                        placeholder="COD To"
                        value={row.cod_to}
                        onChange={e =>
                          handleCashChange(index, "cod_to", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Input
                        placeholder="Charge"
                        value={row.charge}
                        onChange={e =>
                          handleCashChange(index, "charge", e.target.value)
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Button
                        color="danger"
                        onClick={() => removeCashRow(index)}
                      >
                        X
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button color="success" size="sm" onClick={addCashRow}>
                  + Add Row
                </Button>
              </CardBody>
            </Card>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => toggleModal()}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}

export default BusinessRateCard
